import type { LucideIcon } from "lucide-react";
import {
  Clock,
  Waves,
  UtensilsCrossed,
  Wifi,
  Sparkles,
  Dumbbell,
  Car,
  ShieldCheck,
  Users,
  Music,
  Tv,
  Thermometer,
  Heart,
  Star,
  MapPin,
  Phone,
  Building2,
  Bath,
  Bed,
  Wine,
  PartyPopper,
  Plane,
  Trees,
  Sun,
  Coffee,
  Baby,
  PawPrint,
  Camera,
  ConciergeBell,
} from "lucide-react";

/**
 * Chave estável (persistida no banco) que identifica cada ícone disponível
 * para amenidades de empreendimentos. NÃO renomear chaves existentes — isso
 * quebraria registros já gravados; em vez disso, adicione novas chaves.
 */
export type AmenityIconKey =
  | "clock"
  | "waves"
  | "utensils"
  | "wifi"
  | "sparkles"
  | "dumbbell"
  | "car"
  | "shield"
  | "users"
  | "music"
  | "tv"
  | "thermometer"
  | "heart"
  | "star"
  | "mappin"
  | "phone"
  | "building"
  | "bath"
  | "bed"
  | "wine"
  | "party"
  | "plane"
  | "trees"
  | "sun"
  | "coffee"
  | "baby"
  | "pet"
  | "camera"
  | "concierge";

interface AmenityIconDefinition {
  Icon: LucideIcon;
  label: string;
}

/**
 * Catálogo único de ícones disponíveis para amenidades.
 * - `Icon` é o componente Lucide.
 * - `label` é o texto exibido no seletor (Português).
 */
export const AMENITY_ICONS: Record<AmenityIconKey, AmenityIconDefinition> = {
  clock: { Icon: Clock, label: "Relógio (atendimento, horários)" },
  waves: { Icon: Waves, label: "Ondas (piscinas, praia)" },
  utensils: { Icon: UtensilsCrossed, label: "Talheres (gastronomia)" },
  wifi: { Icon: Wifi, label: "Wi-Fi" },
  sparkles: { Icon: Sparkles, label: "Brilho (spa, bem-estar)" },
  dumbbell: { Icon: Dumbbell, label: "Halter (academia, esportes)" },
  car: { Icon: Car, label: "Carro (estacionamento)" },
  shield: { Icon: ShieldCheck, label: "Escudo (segurança)" },
  users: { Icon: Users, label: "Pessoas (família, kids club)" },
  music: { Icon: Music, label: "Música (entretenimento)" },
  tv: { Icon: Tv, label: "TV" },
  thermometer: { Icon: Thermometer, label: "Termômetro (ar-condicionado)" },
  heart: { Icon: Heart, label: "Coração (cuidado, conforto)" },
  star: { Icon: Star, label: "Estrela (destaque)" },
  mappin: { Icon: MapPin, label: "Pin de localização" },
  phone: { Icon: Phone, label: "Telefone" },
  building: { Icon: Building2, label: "Edifício (estrutura)" },
  bath: { Icon: Bath, label: "Banheira" },
  bed: { Icon: Bed, label: "Cama (suítes, hospedagem)" },
  wine: { Icon: Wine, label: "Vinho (bar, lounge)" },
  party: { Icon: PartyPopper, label: "Festa (eventos)" },
  plane: { Icon: Plane, label: "Avião (transfer)" },
  trees: { Icon: Trees, label: "Árvores (natureza)" },
  sun: { Icon: Sun, label: "Sol (clima, beach)" },
  coffee: { Icon: Coffee, label: "Café da manhã" },
  baby: { Icon: Baby, label: "Bebê (espaço infantil)" },
  pet: { Icon: PawPrint, label: "Pet friendly" },
  camera: { Icon: Camera, label: "Câmera (passeios, fotografia)" },
  concierge: { Icon: ConciergeBell, label: "Concierge" },
};

export const DEFAULT_AMENITY_ICON_KEY: AmenityIconKey = "sparkles";

/**
 * Retorna a definição de ícone associada à chave (com fallback seguro).
 */
export const resolveAmenityIcon = (
  key: string | null | undefined,
): AmenityIconDefinition => {
  if (key && (key in AMENITY_ICONS)) {
    return AMENITY_ICONS[key as AmenityIconKey];
  }
  return AMENITY_ICONS[DEFAULT_AMENITY_ICON_KEY];
};

/**
 * Lista ordenada das chaves para uso em seletores.
 */
export const AMENITY_ICON_KEYS: AmenityIconKey[] = Object.keys(
  AMENITY_ICONS,
) as AmenityIconKey[];
